import React, { useState, useEffect, useCallback } from "react";
// import { client } from "@/api";
import { useAuth0 } from "@auth0/auth0-react";
import { errorToast, successToast } from "@/lib/toast";
import { useNavigate } from "react-router-dom";

export const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  const pad = (num) => String(num).padStart(2, "0");

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
  }
  return `${pad(minutes)}:${pad(remainingSeconds)}`;
};

const astrologerId = Math.floor(Math.random() * 10_000_000);

const SessionTimer = ({ isConnected, onDurationChange = () => {} }) => {
  const [seconds, setSeconds] = useState(0);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const { user } = useAuth0();
  const navigate = useNavigate();

  const SESSION_DURATION = 60 * 30; // 30 minutes in seconds
  const REMINDER_INTERVAL = 5 * 60; // 5 minutes in seconds
  const WARNING_TIME = 5 * 60; // Last 5 minutes

  // Send duration to backend
  const sendDurationToBackend = useCallback(async () => {
    try {
      //   await client.post("/api/consultation/call/duration", {
      //     duration: seconds,
      //     astrologerId,
      //     userId: user?.sub,
      //     userEmail: user?.email,
      //   });
      // console.log({ duration: seconds, user });
      successToast("Call duration recorded successfully");
    } catch (error) {
      console.error("Failed to record call duration:", error);
      errorToast("Failed to record call duration");
    } finally {
      navigate("/call-feedback", {
        state: { astrologerId, seconds },
      });
    }
  }, [seconds, astrologerId, user, navigate]);

  // Reminder Modal Component
  const ReminderModal = ({ message }) => {
    useEffect(() => {
      const timer = setTimeout(() => setShowReminderModal(false), 3000);
      return () => clearTimeout(timer);
    }, []);

    return (
      <div className="fixed top-5 right-1/2 translate-x-1/2 bg-white rounded-lg shadow-lg p-4 z-50 animate-fade-in">
        <p className="text-gray-800 font-medium">{message}</p>
      </div>
    );
  };

  // Handle call end
  useEffect(() => {
    if (!isConnected && seconds > 0) {
      sendDurationToBackend();
    }
  }, [isConnected, seconds, sendDurationToBackend]);

  // Timer logic with reminders
  useEffect(() => {
    let intervalId;

    if (isConnected) {
      intervalId = setInterval(() => {
        setSeconds((prev) => {
          const newSeconds = prev + 1;
          onDurationChange(newSeconds);

          // Show 5-minute interval reminders
          if (
            newSeconds % REMINDER_INTERVAL === 0 &&
            newSeconds < SESSION_DURATION - WARNING_TIME
          ) {
            setShowReminderModal(true);
            successToast(`${newSeconds / 60} minutes have passed`);
          }

          // Show warning at last 5 minutes
          if (newSeconds === SESSION_DURATION - WARNING_TIME) {
            errorToast("5 minutes remaining in your session");
          }

          // Show overtime warning when session duration is exceeded
          if (newSeconds === SESSION_DURATION) {
            errorToast("Your session time has exceeded");
          }

          return newSeconds;
        });
      }, 1000);
    } else {
      setSeconds(0);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isConnected, onDurationChange]);

  // Periodic duration updates
  useEffect(() => {
    let updateInterval;

    if (isConnected) {
      updateInterval = setInterval(() => {
        sendDurationToBackend();
      }, 30000);
    }

    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
    };
  }, [isConnected, sendDurationToBackend]);

  // Determine if we're in the warning period or overtime
  const isWarningPeriod =
    SESSION_DURATION - seconds <= WARNING_TIME && seconds <= SESSION_DURATION;
  const isOvertime = seconds > SESSION_DURATION;

  return (
    <>
      <div
        className={`py-3 font-semibold text-lg ${isOvertime
          ? "text-red-600 font-bold"
          : isWarningPeriod
            ? "text-red-500 font-bold animate-pulse"
            : "text-white"
          }`}
      >
        {formatTime(seconds)}
        {isWarningPeriod && (
          <div className="text-sm font-bold text-red-500 mt-1">
            Session ending soon
          </div>
        )}
        {isOvertime && (
          <div className="text-sm font-bold text-red-600 mt-1">
            Session time has exceeded
          </div>
        )}
      </div>
      {showReminderModal && (
        <ReminderModal
          message={`${Math.floor(seconds / 60)} minutes have passed`}
        />
      )}
    </>
  );
};

export default SessionTimer;
